import { DadosListagemContasReceberCliente } from '../../types/ContaReceberTypes';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Button, Stack, Typography, Fade, Divider } from '@mui/material';
import ModalGeneric from '../../Components/generic/ModalGeneric';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useEffect, useState } from 'react';
import StepQrCodePix from './StepQrCodePix';
import StepBoleto from './StepBoleto';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import { toast } from 'react-toastify';
import { copyToClipboard } from '../../helper';

type Props = {
  open: boolean;
  onClose: () => void;
  contaReceber: DadosListagemContasReceberCliente | null;
  onGerarPix: () => void;
  onGerarBoleto: () => void;
};

type MetodoPagamento = 'pix' | 'boleto';

enum PagamentoStep {
  SELECAO = 'SELECAO',
  PIX = 'PIX',
  BOLETO = 'BOLETO'
}

const ModalConfirmarPagamento: React.FC<Props> = ({ open, onClose, contaReceber }) => {
  const [sessao] = useSessao();
  const [metodo, setMetodo] = useState<MetodoPagamento | null>(null);
  const [step, setStep] = useState<PagamentoStep>(PagamentoStep.SELECAO);

  const empresa = sessao?.empresa;
  const cfgCliente = empresa?.cfgEdCliente;

  const temLinhaDigitavel = Boolean(contaReceber?.linhaDigitavel?.trim()?.length);

  const temIntegracaoBoleto = Boolean(empresa?.temIntegracaoBoleto);
  const temIntegracaoPix = Boolean(empresa?.temIntegracaoPix);

  const permiteEmitirBoleto = cfgCliente?.clientePodeEmitirBoleto ?? false;
  const ocultarPixComBoletoRegistrado = cfgCliente?.ocultarPixComBoletoRegistrado ?? true;

  const deveOcultarPix = ocultarPixComBoletoRegistrado && temLinhaDigitavel;

  // Duplicata liquidada aguardando conciliação não pode gerar novo pagamento (Pix ou boleto)
  const bloqueadoConciliacao = Boolean(contaReceber?.aguardandoConciliacao);

  const permiteBoleto =
    !bloqueadoConciliacao && temIntegracaoBoleto && (permiteEmitirBoleto || temLinhaDigitavel);
  const permitePix = !bloqueadoConciliacao && temIntegracaoPix && !deveOcultarPix;

  const handleClose = () => {
    setMetodo('pix');
    setStep(PagamentoStep.SELECAO);
    onClose();
  };

  useEffect(() => {
    if (permitePix) {
      setMetodo('pix');
    } else if (permiteBoleto) {
      setMetodo('boleto');
    } else {
      setMetodo(null);
    }
  }, [permitePix, permiteBoleto]);

  const confirmarPagamento = () => {
    if (metodo === 'pix') {
      if (!sessao?.empresa?.temIntegracaoPix) {
        toast.info('Pagamento via PIX indisponível no momento.');
        return;
      }

      setStep(PagamentoStep.PIX);
      return;
    }

    if (metodo === 'boleto') {
      if (!sessao?.empresa?.temIntegracaoBoleto) {
        toast.info('Pagamento via boleto indisponível. Utilize PIX.');
        return;
      }

      setStep(PagamentoStep.BOLETO);
    }
  };

  const copiarCodigoBarras = async () => {
    if (!contaReceber) {
      toast.error('Fatura não encontrada.');
      return;
    }

    const linhaDigitavel = contaReceber.linhaDigitavel?.trim();

    if (!linhaDigitavel) {
      toast.error('Código de barras indisponível.');
      return;
    }

    copyToClipboard(contaReceber?.linhaDigitavel);
    toast.success('Código de barras copiado!');
  };

  if (!contaReceber) return null;

  return (
    <>
      <ModalGeneric open={open} onClose={handleClose} width="min(420px, 97vw)">
        {step === PagamentoStep.SELECAO && (
          <Box>
            <Box textAlign="center" mb={2}>
              <Typography variant="h6" fontWeight={700}>
                Quase lá!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Escolha como deseja finalizar seu pagamento
              </Typography>
            </Box>

            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 2,
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="body2">Total a pagar</Typography>
              <Typography fontWeight={700}>
                R$ {contaReceber.valores?.restante?.toFixed(2)}
              </Typography>
            </Box>

            <Stack spacing={2}>
              {/* PIX */}
              {!deveOcultarPix && (
                <Fade in timeout={800}>
                  <Box
                    onClick={() => permitePix && setMetodo('pix')}
                    sx={{
                      cursor: permitePix ? 'pointer' : 'not-allowed',
                      borderRadius: 3,
                      p: '2px',
                      background: permitePix
                        ? 'linear-gradient(90deg, #22c55e, #10b981)'
                        : 'linear-gradient(90deg, #e5e7eb, #d1d5db)',
                      animation: permitePix ? 'pulse 2s infinite' : 'none',
                      opacity: permitePix ? 1 : 0.6
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: 'white',
                        border: metodo === 'pix' ? '2px solid #22c55e' : '2px solid transparent',
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: 16,
                          bgcolor: permitePix ? 'success.main' : 'grey.500',
                          color: 'white',
                          fontSize: 10,
                          px: 1.2,
                          py: 0.3,
                          borderRadius: 10,
                          fontWeight: 700
                        }}
                      >
                        {permitePix ? 'RECOMENDADO' : 'INDISPONÍVEL'}
                      </Box>

                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            height: 40,
                            width: 40,
                            bgcolor: permitePix ? 'success.light' : 'grey.500',
                            p: 1,
                            borderRadius: '50%'
                          }}
                        >
                          <QrCode2Icon sx={{ color: 'white' }} />
                        </Box>

                        <Box flex={1}>
                          <Typography fontWeight={700}>Pagar com PIX</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Aprovação imediata
                          </Typography>
                        </Box>

                        {metodo === 'pix' ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <RadioButtonUncheckedIcon color="disabled" />
                        )}
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography
                        variant="caption"
                        color={permitePix ? 'text.secondary' : 'error.main'}
                      >
                        {permitePix
                          ? '🔒 Pagamento seguro e prioritário'
                          : 'PIX indisponível para esta empresa.'}
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              )}

              {/* BOLETO */}
              <Box
                onClick={() => permiteBoleto && setMetodo('boleto')}
                sx={{
                  cursor: permiteBoleto ? 'pointer' : 'not-allowed',
                  p: 2,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: metodo === 'boleto' ? 'grey.500' : 'grey.200',
                  position: 'relative'
                }}
              >
                {!permiteBoleto && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: 16,
                      bgcolor: permitePix ? 'success.main' : 'grey.500',
                      color: 'white',
                      fontSize: 10,
                      px: 1.2,
                      py: 0.3,
                      borderRadius: 10,
                      fontWeight: 700
                    }}
                  >
                    INDISPONÍVEL
                  </Box>
                )}

                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      height: 40,
                      width: 40,
                      bgcolor: permiteBoleto ? '#006effff' : 'grey.500',
                      p: 1,
                      borderRadius: '50%'
                    }}
                  >
                    <ReceiptLongIcon sx={{ color: 'white' }} />
                  </Box>

                  <Box flex={1}>
                    <Typography fontWeight={600}>Pagar com Boleto</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Compensação conforme os prazos bancários.
                    </Typography>
                  </Box>

                  {metodo === 'boleto' ? (
                    <CheckCircleIcon sx={{ color: '#006effff' }} />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" />
                  )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography
                  variant="caption"
                  color={permiteBoleto ? 'warning.main' : 'error.main'}
                  display="block"
                  mt={1}
                >
                  {permiteBoleto
                    ? 'Atenção: o processamento ocorre após confirmação bancária.'
                    : 'Boleto indisponível.'}
                </Typography>
              </Box>

              {temLinhaDigitavel && (
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{ mt: 2, py: 1 }}
                  onClick={copiarCodigoBarras}
                >
                  Copiar código de barras
                </Button>
              )}

              <Button
                fullWidth
                size="small"
                variant="contained"
                sx={{ py: 1 }}
                onClick={confirmarPagamento}
                disabled={!permiteBoleto && !permitePix}
              >
                Confirmar pagamento
              </Button>
            </Stack>
          </Box>
        )}

        {/* Pagamento com pix */}
        {step === PagamentoStep.PIX && sessao?.empresa?.temIntegracaoPix && (
          <StepQrCodePix dados={contaReceber} onClose={handleClose} />
        )}

        {/* Pagamento com boleto */}
        {step === PagamentoStep.BOLETO && sessao?.empresa?.temIntegracaoBoleto && (
          <StepBoleto dados={contaReceber} onClose={handleClose} />
        )}
      </ModalGeneric>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </>
  );
};

export default ModalConfirmarPagamento;
